/**
 * The MCP servers on this machine, desktop only: each with a box that is checked while it is on.
 * Every server that is on goes to the Claude Code the app starts here (`--mcp-config`); one that is
 * off does not. Secrets in a server's `env` or `headers` go to the system's credential store.
 *
 * Three ways to add one: paste its JSON, name its npm package, or give its repository — then the
 * person's own AI reads the README (catalog prompt `mcp.install-from-repo`) and the JSON it writes
 * comes back here to review before adding. "Testar" starts a server for a moment and lists its
 * tools; "Exportar" gives the joined JSON, without secrets, for an IDE.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { inventoryChanged } from '../lib/machineInventory'
import { FileJson, FlaskConical, Loader2, Plus, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { mcpAdd, mcpExport, mcpList, mcpRemove, mcpSetEnabled, mcpTest, type McpServer } from '../lib/desktop'
import { localized, usePromptCatalog } from '../lib/prompts'
import { useOrderResult } from '../lib/useOrderResult'
import { checkRepoUrl, fillUrl, mcpProblem, packageToConfig, readMcpReply, unfilledReferences } from '../lib/skillsAndMcps'
import { mcpConnectPlan, RecommendedMcps } from './RecommendedCatalog'
import type { RecommendedMcp } from '../lib/types'
import { Badge, CodeBlock, EmptyState, ErrorBanner, Panel, Segmented, Skeleton } from './primitives'
import { Modal } from './Modal'

type Mode = 'json' | 'package' | 'repo'

function summary(server: McpServer): string {
  return server.kind === 'stdio' ? [server.command, ...server.args].join(' ') : (server.url ?? '')
}

export function McpServers() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('json')
  const [json, setJson] = useState('')
  const [name, setName] = useState('')
  const [pkg, setPkg] = useState('')
  const [url, setUrl] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [added, setAdded] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [tests, setTests] = useState<Record<string, { running: boolean; text?: string; ok?: boolean }>>({})
  const [exported, setExported] = useState<string | null>(null)
  const { data: catalog } = usePromptCatalog()
  const { order, finished } = useOrderResult(orderId)

  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['mcp-servers'], queryFn: mcpList })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['mcp-servers'] })
    inventoryChanged()
  }

  // The configuration the local AI wrote comes back into the JSON box, to review before adding.
  const fromRepo = finished ? readMcpReply(order?.lastReply ?? order?.lastReport) : null
  useEffect(() => {
    if (fromRepo) {
      setJson(fromRepo)
      setMode('json')
    }
  }, [fromRepo])

  const add = useMutation({
    mutationFn: ({ text, bare }: { text: string; bare: string | null }) => mcpAdd(text, bare),
    onMutate: () => {
      setProblem(null)
      setAdded(null)
    },
    onSuccess: (names) => {
      setAdded(t('mcp.added', { names: names.join(', ') }))
      setJson('')
      setName('')
      setPkg('')
      setOrderId(null)
      refresh()
    },
    onError: (e) => setProblem(mcpProblem(t, e)),
  })

  const toggle = useMutation({
    mutationFn: (server: McpServer) => mcpSetEnabled(server.name, !server.enabled),
    onError: (e) => setProblem(mcpProblem(t, e)),
    onSettled: refresh,
  })

  const remove = useMutation({
    mutationFn: (server: McpServer) => mcpRemove(server.name),
    onError: (e) => setProblem(mcpProblem(t, e)),
    onSettled: refresh,
  })

  const ask = useMutation({
    mutationFn: async (repo: string) => {
      const prompt = catalog?.prompts.find((p) => p.key === 'mcp.install-from-repo')
      if (!prompt) throw new Error('catalog')
      return api.workOrders.create({ kind: 'Once', instructions: fillUrl(prompt.body, repo), title: `${localized(prompt.title, locale)}: ${repo.trim()}`.slice(0, 200), repositoryId: null })
    },
    onSuccess: (created) => {
      setOrderId(created.id)
      setUrl('')
    },
    onError: (e) => setProblem(e instanceof Error && e.message === 'catalog' ? t('skillsMachine.noCatalog') : t('error.generic')),
  })

  const test = async (server: McpServer) => {
    setTests((all) => ({ ...all, [server.name]: { running: true } }))
    try {
      const result = await mcpTest(server.name)
      const text = result.ok
        ? result.tools.length > 0 ? t('mcp.testOk', { count: result.tools.length, tools: result.tools.slice(0, 12).join(', ') }) : t('mcp.testOkNoTools')
        : mcpProblem(t, result.error ?? '')
      setTests((all) => ({ ...all, [server.name]: { running: false, ok: result.ok, text } }))
    } catch (e) {
      setTests((all) => ({ ...all, [server.name]: { running: false, ok: false, text: mcpProblem(t, e) } }))
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    if (mode === 'json') {
      if (json.trim()) add.mutate({ text: json, bare: name.trim() || null })
    } else if (mode === 'package') {
      const config = packageToConfig(pkg)
      if (!config) setProblem(t('mcp.error.invalid-package'))
      else add.mutate({ text: config.json, bare: null })
    } else {
      const check = checkRepoUrl(url)
      if (check !== 'ok') setProblem(t(check === 'credentials' ? 'skills.error.url_has_credentials' : 'skills.error.invalid_url'))
      else ask.mutate(url)
    }
  }

  const references = mode === 'json' ? unfilledReferences(json) : []

  // "Conectar" on a recommended entry (docs/refactor/42): a verified configuration goes straight in;
  // one with a secret lands in the box with the ${VALUE} to fill; one without a published command
  // goes to the person's own AI to write from the repository. Never an MCP with an empty secret.
  const connect = (mcp: RecommendedMcp) => {
    setProblem(null)
    setAdded(null)
    const plan = mcpConnectPlan(mcp)
    if (plan.kind === 'add') add.mutate({ text: plan.config, bare: mcp.key })
    else if (plan.kind === 'fill') {
      setMode('json')
      setJson(plan.config)
      setName(mcp.key)
      setAdded(t('recommended.fillFirst', { values: unfilledReferences(plan.config).join(', ') }))
    } else {
      ask.mutate(plan.repo)
    }
  }

  return (
    <div className="stack">
      <Panel title={t('mcp.addTitle')}>
        <form className="stack" onSubmit={submit}>
          <div className="skills-tabs">
            <Segmented<Mode>
              value={mode}
              onChange={(next) => {
                setMode(next)
                setProblem(null)
              }}
              options={[
                { value: 'json', label: t('mcp.mode.json') },
                { value: 'package', label: t('mcp.mode.package') },
                { value: 'repo', label: t('mcp.mode.repo') },
              ]}
            />
          </div>
          {mode === 'json' && (
            <>
              <span className="tiny muted">{t('mcp.jsonHint')}</span>
              <textarea className="input mono small" rows={8} value={json} onChange={(e) => setJson(e.target.value)} placeholder={'{ "mcpServers": { "filesystem": { "command": "npx", "args": ["-y", "@modelcontextprotocol/server-filesystem", "C:/projetos"] } } }'} aria-label={t('mcp.mode.json')} />
              <input className="input mono" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('mcp.namePlaceholder')} aria-label={t('mcp.namePlaceholder')} />
              {references.length > 0 && <div className="notice small">{t('mcp.fillIn', { keys: references.join(', ') })}</div>}
            </>
          )}
          {mode === 'package' && (
            <>
              <span className="tiny muted">{t('mcp.packageHint')}</span>
              <input className="input mono" value={pkg} onChange={(e) => setPkg(e.target.value)} placeholder="@modelcontextprotocol/server-filesystem C:/projetos" aria-label={t('mcp.mode.package')} />
            </>
          )}
          {mode === 'repo' && (
            <>
              <span className="tiny muted">{t('mcp.repoHint')}</span>
              <input className="input mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://github.com/modelcontextprotocol/servers" aria-label={t('mcp.mode.repo')} />
            </>
          )}
          <div className="row row--tight">
            <button type="submit" className="btn btn--primary" disabled={add.isPending || ask.isPending}>
              <Plus size={14} /> {mode === 'repo' ? t('mcp.askAi') : t('mcp.add')}
            </button>
          </div>
          {orderId && !finished && <div className="notice small row row--tight"><Loader2 size={13} className="spin" /> {t('mcp.reading')}</div>}
          {orderId && finished && !fromRepo && <div className="notice small">{t('mcp.readFailed')}</div>}
          {orderId && finished && fromRepo && mode === 'json' && <div className="notice small">{t('mcp.review')}</div>}
          {added && <div className="notice small">{added}</div>}
          {problem && <ErrorBanner message={problem} />}
        </form>
      </Panel>

      <RecommendedMcps onConnect={connect} />

      <Panel
        title={t('mcp.title')}
        subtitle={t('mcp.hint')}
        actions={
          <button
            type="button"
            className="btn btn--sm"
            disabled={!data?.some((s) => s.enabled)}
            onClick={() => void mcpExport().then(setExported).catch((e: unknown) => setProblem(mcpProblem(t, e)))}
          >
            <FileJson size={13} /> {t('mcp.export')}
          </button>
        }
      >
        <div className="stack">
          {error && <ErrorBanner message={mcpProblem(t, error)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />}
          {isLoading && <Skeleton height={60} />}
          {data && data.length === 0 && <EmptyState title={t('mcp.emptyTitle')} body={t('mcp.emptyBody')} />}
          {data && data.length > 0 && (
            <ul className="machine-list">
              {data.map((server) => {
                const result = tests[server.name]
                const secrets = [...server.env_keys, ...server.header_keys]
                return (
                  <li key={server.name} className="machine-row">
                    <label className="machine-row__check">
                      <input type="checkbox" checked={server.enabled} disabled={toggle.isPending} onChange={() => toggle.mutate(server)} aria-label={t(server.enabled ? 'mcp.turnOff' : 'mcp.turnOn', { name: server.name })} />
                    </label>
                    <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                      <div className="row row--tight row--wrap">
                        <strong className="mono small">{server.name}</strong>
                        <Badge tone="neutral">{server.kind === 'stdio' ? t('mcp.kindLocal') : t('mcp.kindRemote')}</Badge>
                        {!server.enabled && <Badge tone="warn">{t('skillsMachine.off')}</Badge>}
                      </div>
                      <span className="tiny muted mono truncate" title={summary(server)}>{summary(server)}</span>
                      {secrets.length > 0 && <span className="tiny muted">{t('mcp.secrets', { keys: secrets.join(', ') })}</span>}
                      {result?.text && <span className={`tiny ${result.ok ? 'skills-notes__ok' : 'skills-notes__bad'}`}>{result.text}</span>}
                    </div>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => void test(server)} disabled={result?.running}>
                      {result?.running ? <Loader2 size={13} className="spin" /> : <FlaskConical size={13} />} {t('mcp.test')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost btn--icon"
                      aria-label={t('mcp.remove')}
                      title={t('mcp.remove')}
                      onClick={() => {
                        if (window.confirm(t('mcp.removeConfirm', { name: server.name }))) remove.mutate(server)
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </Panel>

      {exported !== null && (
        <Modal title={t('mcp.exportTitle')} onClose={() => setExported(null)} wide>
          <p className="small muted" style={{ marginTop: 0 }}>{t('mcp.exportHint')}</p>
          <CodeBlock code={exported} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
        </Modal>
      )}
    </div>
  )
}
